import { useEffect, useRef, useState } from 'react';
import { injectTSDI } from '../../../../core/tsdi';
import { Api } from '../../../../core/api';
import { fetchAndParseGpx } from '../../flags-tool/utils/GpxMap.utils';
import { parseGpxPoints } from './parseGpxPoints';
import { enrichTrack } from './enrichTrack';
import { computeTrackStats } from './trackStats';
import { EnrichedTrack } from '../types';

interface TrackQueryState {
    loading: boolean;
    error: string | null;
    track: EnrichedTrack | null;
    noGpx: boolean;
}

const INITIAL_STATE: TrackQueryState = {
    loading: true,
    error: null,
    track: null,
    noGpx: false
};

export function useTrackData(workoutId: number | null): TrackQueryState {
    const [state, setState] = useState<TrackQueryState>(INITIAL_STATE);
    const cancelledRef = useRef(false);

    useEffect(() => {
        cancelledRef.current = false;
        if (workoutId === null) {
            setState(INITIAL_STATE);
            return;
        }

        setState({
            loading: true,
            error: null,
            track: null,
            noGpx: false
        });

        (async () => {
            try {
                const api = injectTSDI(Api);
                const details =
                    await api.fitnessActivityClient.admin.activitySupportControllerGetActivity(
                        workoutId
                    );

                const url = details.data.gpxFileUrl;
                if (!url) {
                    if (cancelledRef.current) return;
                    setState({
                        loading: false,
                        error: null,
                        track: null,
                        noGpx: true
                    });
                    return;
                }

                const xml = await fetchAndParseGpx(url);
                if (cancelledRef.current) return;

                const points = enrichTrack(parseGpxPoints(xml));
                const stats = computeTrackStats(points);
                setState({
                    loading: false,
                    error: null,
                    track: { workoutId, points, stats },
                    noGpx: false
                });
            } catch (e: unknown) {
                if (cancelledRef.current) return;
                const msg =
                    e instanceof Error ? e.message : 'Failed to load GPX track';
                setState({
                    loading: false,
                    error: msg,
                    track: null,
                    noGpx: false
                });
            }
        })();

        return () => {
            cancelledRef.current = true;
        };
    }, [workoutId]);

    return state;
}
